import { Icon } from "@src/components/deprecated/Icon/Icon";
import { Text } from "@src/components/deprecated/Text/Text";
import { ReactComponent as CrossSVG } from "@svg/cross.svg";
import { GenericComponentProps, makeStyles } from "@utils/genericComponentProps";
import React, { ReactNode, forwardRef, useEffect, useState } from "react";
import styles from "./TextInputField.module.css";

export type TextInputFieldProps = GenericComponentProps & {
  value: string | undefined;
  onChange: (value: string) => unknown;

  onEnterPressed?: () => void;
  validate?: (value: string) => boolean | string;
  onClick?: React.InputHTMLAttributes<HTMLInputElement>["onClick"];

  label?: ReactNode;

  helperText?: ReactNode;
  helperTextColor?: string;
  helperTextDataCy?: string;
  trailingText?: ReactNode;
  disabled?: boolean;
  placeholder?: string;

  type?: React.InputHTMLAttributes<HTMLInputElement>["type"];
  required?: boolean;
  fullWidth?: boolean;
  clearable?: boolean;
  leadingIconName?: string;
  trailingIconName?: string;
  trailingIconType?: "filled" | "outlined";
  trailingIconSize?: string | number;
  trailingIconColor?: string;
  className?: string;

  size?: "md" | "sm";
  dataCy?: string;

  inputProps?: Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange" | "size" | "value">;
};

/**
 * @deprecated use TextField instead
 */
export const TextInputField = forwardRef(
  (
    {
      disabled,
      label,
      type,
      placeholder,
      helperText,
      helperTextColor,
      helperTextDataCy,
      size,
      value,
      onChange,
      validate,
      trailingText,
      trailingIconName,
      trailingIconType = "filled",
      trailingIconSize = "24px",
      trailingIconColor = "var(--gray-color)",
      onClick,
      inputProps,
      ...props
    }: TextInputFieldProps,
    ref: any
  ) => {
    const [error, setError] = useState<string | boolean>(false);

    const containerClassName = `${styles.inputContainer} ${props.className ?? ""} ${styles[size ?? "md"]} ${disabled ? styles.disabled : ""}`;

    const containerStyle = makeStyles(props, {
      width: props.fullWidth ? "100%" : "200px",
      style: {
        display: "flex",
        alignItems: "center",
      },
    });

    useEffect(() => {
      if (validate) {
        setError(validate(value ?? ""));
      }
    }, [validate, value]);

    return (
      <div style={{ width: "100%" }}>
        {label && (
          <Text
            size="sm"
            marginBottom={2}
            trailingIcon={props.required ? { name: "star", size: "8px", marginLeft: 1, color: "var(--primary-color)" } : undefined}
          >
            {label}
          </Text>
        )}

        <div className={containerClassName} style={containerStyle}>
          {props.leadingIconName && <Icon name={props.leadingIconName} size="1.1em" marginTop={4} marginRight={4} />}

          <input
            className={styles.input}
            type={type ?? "text"}
            onClick={onClick}
            onKeyDown={(event) => {
              if (event.key === "Enter") props.onEnterPressed?.();
            }}
            placeholder={placeholder}
            value={value}
            onChange={(event) => onChange(event.target.value)}
            ref={ref}
            disabled={disabled}
            data-cy={props.dataCy}
            {...inputProps}
          />

          {trailingText && (
            <Text size="sm" marginLeft={4} marginRight={8} color="secondary" textAlign="right" noWrap>
              {trailingText}
            </Text>
          )}

          {trailingIconName && (
            <Icon color={trailingIconColor} marginRight={8} name={trailingIconName} type={trailingIconType} size={trailingIconSize} marginLeft={4} />
          )}

          {props.clearable && value && (
            <CrossSVG scale="24px" color="var(--text-2-color)" onClick={() => onChange("")} style={{ marginRight: "10px" }} />
          )}
        </div>

        {error && typeof error === "string" ? (
          <Text size="sm" color="secondary" marginTop={4} customColor="var(--main-red-700)">
            {error}
          </Text>
        ) : helperText ? (
          <Text dataCy={helperTextDataCy} size="sm" color="secondary" marginTop={4} customColor={helperTextColor}>
            {helperText}
          </Text>
        ) : null}
      </div>
    );
  }
);

